<!-- =========================================================================================
  File Name: UserEditTabInformation.vue
  Description: User Edit Information Tab content
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
	<div slot="no-body" class="tabs-container px-6">
		<div class="tab-text">
			<div id="user-edit-tab-info">
				<div class="vx-row">
					<div class="vx-col w-full">
						<vx-card :cardBorder="true" :noShadow="true" :title="$t('GeneralInformation')">
							<form>
								<div class="vx-row">
									<div
										class="vx-col w-full mt-2"
										:class="{
											'md:w-1/2':
												!selectedOptimizationApi ||
												!['OPENROUTE', 'SERVIROUTES', 'TOUR_PLANNING', 'GMPRO'].includes(
													selectedOptimizationApi.value
												),
										}"
									>
										<label class="vs-input--label">{{ $t("Company") }}</label>
										<v-select
											name="company"
											v-model="selectedCompany"
											v-validate="'required'"
											value="id"
											label="name"
											:clearable="false"
											:options="listCompanies"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											@input="removeOptimizerExist(selectedCompany)"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
										<span class="text-danger text-sm" v-show="errors.has('optimizer')">{{
											$t("Routing.AssignColumns.Text1") + " " + $t("Routing.AssignColumns.Text2")
										}}</span>
									</div>
									<div class="vx-col md:w-1/4 w-full mt-2">
										<label class="vs-input--label">{{ $t("Routing.Optimizer") }}</label>
										<v-select
											v-model="selectedOptimizationApi"
											v-validate="'required'"
											name="optimizer"
											:clearable="false"
											:options="optimizerList"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
										<span class="text-danger text-sm" v-show="errors.has('optimizer')">{{
											$t("Routing.AssignColumns.Text1") + " " + $t("Routing.AssignColumns.Text2")
										}}</span>
									</div>
									<div
										v-if="
											selectedOptimizationApi &&
											['OPENROUTE', 'SERVIROUTES', 'TOUR_PLANNING', 'GMPRO'].includes(
												selectedOptimizationApi.value
											)
										"
										class="vx-col md:w-1/2 w-full mt-2"
									>
										<label class="vs-input--label">{{ $t("TypeOfService") }}</label>
										<v-select
											name="service"
											v-model="selectedService"
											v-validate="'required'"
											:clearable="false"
											:options="filteredServiceList"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
									</div>
									<div class="vx-col md:w-1/4 w-full mt-2">
										<label class="vs-input--label"> {{ $t("SessionApi.ExpireDate") }}</label>
										<datepicker
											name="enddate"
											v-validate="'validatorExpireDate'"
											v-model="optimizerManager.date_end"
											:language="$vs.rtl ? langHe : langEn"
											:disabledDates="disabledDatesTo"
										></datepicker>
										<span class="text-danger text-sm" v-show="errors.has('enddate')">{{
											$t("Routing.AssignColumns.Text1") + " " + $t("Routing.AssignColumns.Text2")
										}}</span>
									</div>
								</div>
								<div class="vx-row mt-2">
									<div class="vx-col md:w-1/5 w-full">
										<label class="vs-input--label">
											{{ $t("OptimizerManager.PointsPerDay") }}</label
										>
										<vs-input
											name="pointsperday"
											class="w-full"
											v-model="optimizerManager.points_per_day"
											v-validate="'max_value:2147483647'"
											@keypress="validateAmountNumber"
										/>
										<span class="text-danger text-sm" v-show="errors.has('pointsperday')">{{
											$t(
												"OptimizerManager.ErrorMessage.ThePointsperdayFieldMustBe2147483647OrLess"
											)
										}}</span>
									</div>
									<div class="vx-col md:w-1/5 w-full">
										<label class="vs-input--label">
											{{ $t("OptimizerManager.PointsPerWeek") }}</label
										>
										<vs-input
											name="pointsperweek"
											class="w-full"
											v-model="optimizerManager.points_per_week"
											v-validate="'max_value:2147483647'"
											@keypress="validateAmountNumber"
										/>
										<span class="text-danger text-sm" v-show="errors.has('pointsperweek')">{{
											$t(
												"OptimizerManager.ErrorMessage.ThePointsPerWeekFieldShouldBe2147483647OrLower"
											)
										}}</span>
									</div>
									<div class="vx-col md:w-1/5 w-full">
										<label class="vs-input--label">
											{{ $t("OptimizerManager.PointsPerMonth") }}</label
										>
										<vs-input
											name="pointsperMonth"
											class="w-full"
											v-model="optimizerManager.points_per_month"
											v-validate="'max_value:2147483647'"
											@keypress="validateAmountNumber"
										/>
										<span class="text-danger text-sm" v-show="errors.has('pointsperMonth')">{{
											$t(
												"OptimizerManager.ErrorMessage.ThePointsPerMonthFieldMustBe2147483647OrLower"
											)
										}}</span>
									</div>
									<div class="vx-col md:w-1/5 w-full">
										<label class="vs-input--label">
											{{ $t("OptimizerManager.PointsSubscriptionMonthly") }}</label
										>
										<vs-input
											name="pointssubscriptionmonthly"
											class="w-full"
											v-model="optimizerManager.points_subscription_monthly"
											v-validate="'max_value:2147483647'"
											@keypress="validateAmountNumber"
										/>
										<span
											class="text-danger text-sm"
											v-show="errors.has('pointssubscriptionmonthly')"
											>{{
												$t(
													"OptimizerManager.ErrorMessage.ThePointsSubscriptionMonthlyFieldMustBe2147483647OrLess"
												)
											}}</span
										>
									</div>
									<div class="vx-col md:w-1/5 w-full">
										<label class="vs-input--label"> {{ $t("OptimizerManager.PointsTotal") }}</label>
										<vs-input
											name="pointsTotal"
											class="w-full"
											icon-no-border
											v-model="optimizerManager.points_total"
											v-validate="'max_value:2147483647'"
											@keypress="validateAmountNumber"
										/>
										<span class="text-danger text-sm" v-show="errors.has('pointsTotal')">{{
											$t(
												"OptimizerManager.ErrorMessage.TheTotalPointsFieldMustBe2147483647OrLower"
											)
										}}</span>
									</div>
								</div>
								<div class="vx-row mt-2">
									<div class="vx-col md:w-1/2 w-full">
										<label class="vs-input--label">{{ $t("ApiKey") }}</label>
										<vs-input
											class="w-full"
											v-model="optimizerManager.api_key"
											name="apikey"
											:danger="dangerInput.apikey.show"
											:danger-text="dangerInput.apikey.text"
											@focus="() => (dangerInput.apikey.show = false)"
										/>
									</div>
									<div class="vx-col md:w-1/4 w-full">
										<div class="mt-8 flex items-center justify-start">
											<label class="vs-input--label">{{
												$t("OptimizerManager.EnableOptimizationByTheWebPlatform")
											}}</label>
											<vs-switch
												class="ml-2"
												color="success"
												v-model="optimizerManager.web_enable"
											/>
										</div>
									</div>
									<div class="vx-col md:w-1/4 w-full">
										<div class="mt-8 flex items-center justify-start">
											<label class="vs-input--label">{{
												$t("OptimizerManager.EnableOptimizationByAPIConsumption")
											}}</label>
											<vs-switch
												class="ml-2"
												color="success"
												v-model="optimizerManager.api_enable"
											/>
										</div>
									</div>
								</div>
							</form>
						</vx-card>
					</div>
				</div>
			</div>
		</div>
		<div class="vx-row pt-4">
			<div class="vx-col w-full">
				<div class="flex flex-wrap items-center justify-end">
					<vs-button
						class="ml-auto"
						color="danger"
						@click.stop="$router.push({name: 'app-optimizer-manager-list'})"
						>{{ $t("Cancel") }}</vs-button
					>
					<vs-button class="ml-4" :disabled="!validateForm" @click.prevent="save_changes()">{{
						$t("Save")
					}}</vs-button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {optimizerManagerMixin} from "../model/OptimizerManagerMixin.js";

export default {
	mixins: [optimizerManagerMixin],
};
</script>
